import { defineStore } from 'pinia';
import {
  paginatedFeedOnContainerScroll,
  paginatedFeedRecoverIfStuck,
} from '@/domain/market/paginated-feed-core.js';
import { runPaginatedFeedFetchPage, runPaginatedFeedLoadMore } from '@/stores/feed-fetch-error.js';
import { shouldIgnoreFeedScrollLoad } from '@/composables/feed-viewport.js';
import {
  createCryptoFeedFetchOptions,
  createCryptoFeedGroupFetchOptions,
} from '@/domain/crypto/crypto-feed-adapter.js';
import { paginatedFeedIsManualLoadRequired } from '@/domain/market/paginated-feed-pagination.js';
import { rearrangeCryptoMinuteUpdownFeedRows } from '@/domain/crypto/crypto-minute-feed-pagination.js';
import { getClobNowSec } from '@/domain/crypto/polymarket-clob-clock.js';
import { runFeedGroupThenSilentPage } from '@/domain/crypto/crypto-group-by-slug-feed.js';

const cryptoFetchOptions = createCryptoFeedFetchOptions();
const cryptoGroupFetchOptions = createCryptoFeedGroupFetchOptions();

/**
 * Crypto 市场 Feed 状态源（paginated-feed-core + crypto-feed-adapter）。
 * 首屏优先 cryptoPageGroupBySlug，再静默 cryptoPage 归集回填。
 */
export const useCryptoFeedStore = defineStore('cryptoFeed', {
  state: () => ({
    initialized: false,
    selectedFilter: 'all',
    rawRows: [],
    listLoading: false,
    loadingMore: false,
    hasMore: true,
    pageNum: 0,
    loadGeneration: 0,
    reloadToken: 0,
    lastFetchPlatform: 'pc',
    autoAppendCount: 0,
    appendAutoLoadBlocked: false,
    listFetchError: null,
    /** Group 首屏后 page 静默回填中，暂禁 loadMore */
    silentBackfillPending: false,
  }),

  getters: {
    showListEmpty(state) {
      return !state.listLoading && !state.loadingMore && state.rawRows.length === 0;
    },
    manualLoadRequired(state) {
      return paginatedFeedIsManualLoadRequired(state);
    },
  },

  actions: {
    setLastFetchPlatform(isH5) {
      this.lastFetchPlatform = isH5 ? 'h5' : 'pc';
    },

    clear() {
      this.loadGeneration += 1;
      this.rawRows = [];
      this.hasMore = false;
      this.pageNum = 0;
      this.listLoading = false;
      this.loadingMore = false;
      this.initialized = false;
      this.appendAutoLoadBlocked = false;
      this.listFetchError = null;
      this.silentBackfillPending = false;
    },

    async selectFilter(key) {
      const next = String(key ?? '').trim() || 'all';
      if (next === this.selectedFilter && this.initialized) return;
      this.selectedFilter = next;
      this.reloadToken += 1;
      this.initialized = false;
      this.silentBackfillPending = false;
      await this.fetchPage(false);
    },

    recoverIfStuck() {
      paginatedFeedRecoverIfStuck(this);
    },

    async ensureInit(filter = 'all') {
      this.selectedFilter = String(filter ?? '').trim() || 'all';
      if (this.initialized && this.rawRows.length) return;
      this.recoverIfStuck();
      if (this.initialized && this.rawRows.length) return;
      await this.fetchPage(false);
    },

    async fetchPage(append = false, options = {}) {
      await runFeedGroupThenSilentPage(this, {
        append,
        options,
        runFetchPage: runPaginatedFeedFetchPage,
        groupFetchOptions: cryptoGroupFetchOptions,
        pageFetchOptions: cryptoFetchOptions,
        afterGroupBootstrap: () => {
          if (this.initialized) this.rearrangeMinuteUpdownRowsLocal();
        },
      });
      if (!append && !options.silent && this.initialized) {
        this.rearrangeMinuteUpdownRowsLocal();
      }
    },

    async loadMore(options = {}) {
      if (this.silentBackfillPending) return;
      await runPaginatedFeedLoadMore(this, {
        ...cryptoFetchOptions,
        ...options,
      });
    },

    /**
     * 整 5 分钟网格：原位置把已结束分钟盘滚到当前 live 轮次，不打接口、不删卡。
     * @returns {boolean}
     */
    rearrangeMinuteUpdownRowsLocal() {
      if (!this.initialized || this.listLoading || this.loadingMore) return false;
      const prev = Array.isArray(this.rawRows) ? this.rawRows : [];
      const next = rearrangeCryptoMinuteUpdownFeedRows(prev, getClobNowSec());
      const unchanged =
        next.length === prev.length && next.every((row, index) => row === prev[index]);
      if (unchanged) return false;
      this.rawRows = next;
      return true;
    },

    rowKey(row) {
      return String(row?.id ?? row?.collectionId ?? row?.marketId ?? '');
    },

    onContainerScroll(container, options = {}) {
      if (this.silentBackfillPending) return;
      paginatedFeedOnContainerScroll(this, container, {
        ...options,
        fetchOptions: cryptoFetchOptions,
        shouldIgnoreScrollLoad: shouldIgnoreFeedScrollLoad,
      });
    },
  },
});
